import type { FC, MouseEvent, ReactNode } from 'react'

import { useRef, useState } from 'react'
import Image from 'next/image'
import { ArrowUpRight } from 'lucide-react'
import { cn } from '@/lib/utils'
import { pad } from '@/lib/format'
import { gsap, useGSAP } from '@/lib/gsap'
import { TransitionLink } from '@/components/generals/transition-link'
import { services } from '@/constants/services'

/**
 * Editorial list of every service. On desktop a photo of the hovered service
 * follows the cursor.
 */
export const ServiceIndexList: FC = (): ReactNode => {
    const [hovered, setHovered] = useState<number | null>(null)
    const listRef = useRef<HTMLDivElement>(null)
    const previewRef = useRef<HTMLDivElement>(null)
    const moveX = useRef<((value: number) => void) | null>(null)
    const moveY = useRef<((value: number) => void) | null>(null)

    useGSAP(() => {
        if (!previewRef.current) return

        moveX.current = gsap.quickTo(previewRef.current, 'x', { duration: 0.6, ease: 'power3.out' })
        moveY.current = gsap.quickTo(previewRef.current, 'y', { duration: 0.6, ease: 'power3.out' })
    }, { scope: listRef })

    const handleMove = (event: MouseEvent<HTMLDivElement>) => {
        const list = listRef.current

        if (!list) return

        const rect = list.getBoundingClientRect()

        moveX.current?.(event.clientX - rect.left)
        moveY.current?.(event.clientY - rect.top)
    }

    return (
        <div ref={listRef} className='relative' onMouseMove={handleMove} onMouseLeave={() => setHovered(null)}>
            <div
                ref={previewRef}
                aria-hidden='true'
                className={cn(
                    'pointer-events-none absolute left-0 top-0 z-20 hidden h-[15rem] w-[21rem] -translate-x-1/2 -translate-y-1/2 overflow-hidden rounded-[22px] shadow-[0_30px_70px_-30px_rgba(21,23,26,0.6)] transition-[opacity,scale] duration-500 ease-expo lg:block',
                    hovered === null ? 'scale-75 opacity-0' : 'scale-100 opacity-100'
                )}
            >
                {services.map((service, index) => (
                    <Image
                        key={service.slug}
                        src={service.image}
                        alt=''
                        fill
                        sizes='336px'
                        className={cn('object-cover transition-opacity duration-500', hovered === index ? 'opacity-100' : 'opacity-0')}
                    />
                ))}
            </div>

            <ul className='border-t border-ink/10'>
                {services.map((service, index) => (
                    <li key={service.slug} onMouseEnter={() => setHovered(index)}>
                        <TransitionLink
                            href={service.href}
                            className='group grid grid-cols-12 items-center gap-x-4 gap-y-3 border-b border-ink/10 py-7 md:py-9'
                        >
                            <span className='col-span-2 font-mono text-[12px] text-slate md:col-span-1'>
                                {pad(index + 1)}
                            </span>

                            <span className='col-span-10 flex items-center gap-4 md:col-span-5'>
                                <span className='relative size-14 shrink-0 overflow-hidden rounded-xl bg-sand lg:hidden'>
                                    <Image src={service.image} alt='' fill sizes='56px' className='object-cover' />
                                </span>

                                <span className='font-heading text-[1.6rem] font-semibold leading-[1.05] tracking-[-0.035em] text-ink transition-[color,transform,translate,scale,rotate] duration-500 ease-expo group-hover:translate-x-2 group-hover:text-ember md:text-[2.4rem]'>
                                    {service.title}
                                </span>
                            </span>

                            <span className='col-span-12 text-[15px] leading-relaxed text-slate md:col-span-3'>
                                {service.summary}
                            </span>

                            <span className='col-span-12 flex items-center justify-between gap-6 md:col-span-3 md:justify-end'>
                                <span className='text-right font-mono text-[12px] uppercase tracking-[0.12em] text-ink'>
                                    <span className='block'>{service.priceLabel}</span>
                                    <span className='block text-slate'>{service.meta}</span>
                                </span>

                                <span className='flex size-12 shrink-0 items-center justify-center rounded-full border border-ink/15 transition-[background-color,border-color,color,transform,translate,scale,rotate] duration-500 ease-expo group-hover:rotate-45 group-hover:border-ember group-hover:bg-ember group-hover:text-white'>
                                    <ArrowUpRight className='size-4' aria-hidden='true' />
                                </span>
                            </span>
                        </TransitionLink>
                    </li>
                ))}
            </ul>
        </div>
    )
}
